import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { CdkListbox, CdkOption } from '@angular/cdk/listbox';
import { AfterContentInit, Directive, Input, QueryList } from '@angular/core';

// Checked via inline style / `hidden` only: the panel lives in an overlay that is detached while closed,
// so layout-based checks (offsetParent, computed style) would treat every option as hidden.
const isHiddenOption = (element: HTMLElement): boolean => element.hidden || element.style.display === 'none';

@Directive({
  selector: '[cslCdkListbox]',
  exportAs: 'cslCdkListbox',
  standalone: true,
})
export class CslCdkListboxDirective<T = unknown> extends CdkListbox<T> implements AfterContentInit {
  @Input() mode: 'default' | 'autocomplete' | 'select' = 'default';

  keyManager!: ActiveDescendantKeyManager<CdkOption<T>>;
  items?: QueryList<CdkOption<T>>;

  override ngAfterContentInit(): void {
    super.ngAfterContentInit();

    this.items = this.options;
    this.keyManager = this.listKeyManager;

    switch (this.mode) {
      case 'autocomplete': {
        this.useActiveDescendant = true; // enable keydown navigation on panel
        this.enabledTabIndex = -1; // normalize focus change behavior;
        this.listKeyManager.withHomeAndEnd(false).withAllowedModifierKeys([]); // override default keybindings
        break;
      }
      case 'select': {
        this.useActiveDescendant = true; // enable keydown navigation on panel
        // options hidden by consumers (e.g. filtered out by search) must not be reachable via keyboard/typeahead
        this.listKeyManager.skipPredicate(
          (option) => (!this.navigateDisabledOptions && option.disabled) || isHiddenOption(option.element),
        );
        break;
      }
    }
  }

  override _handleKeydown(event: KeyboardEvent) {
    switch (this.mode) {
      case 'select': {
        // empty: select component has own handler
        break;
      }
      default: {
        super._handleKeydown(event);
      }
    }
  }

  override selectValue(...values: T[]): void {
    if (values.length > 1 && this.multiple) {
      this.selectionModel.select(...values);
    }
    super.selectValue(values[0]);
  }

  // Confirmed via testing (NVDA + Chrome) that rendering aria-activedescendant - even only while open -
  // causes screen readers to misannounce ambient landmark context once the popup closes. Navigation is
  // announced explicitly via LiveAnnouncer instead (see SelectComponent), so this is never rendered.
  protected override _getAriaActiveDescendant(): string | null | undefined {
    return null;
  }

  selectActiveOptionValue() {
    const activeItem = this.listKeyManager.activeItem;
    if (!activeItem) return;

    this.valueChange.next({
      value: [activeItem.value],
      listbox: this,
      option: activeItem,
    });
  }
}
